import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { Zap, Banknote, Clock, Phone, MessageCircle, ChevronDown, DollarSign, Truck, FileText } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";
import { PHONE_TEL, WA_URL, IMAGES } from "@/lib/constants";

const TRUST_ICONS = [DollarSign, Truck, FileText, Clock];

const lineAnim = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { delay: 0.2 + i * 0.13, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
    }),
};

export const Hero = () => {
    const { t } = useLang();
    const cardRef = useRef(null);
    const sectionRef = useRef(null);

    const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 16 });
    const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 16 });

    const onMouseMove = (e) => {
        const r = cardRef.current.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        ry.set(px * 9);
        rx.set(-py * 9);
    };
    const onMouseLeave = () => {
        rx.set(0);
        ry.set(0);
    };

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);

    const onCall = () => scrollToId("#get-offer");

    return (
        <section
            id="top"
            ref={sectionRef}
            data-testid="hero-section"
            className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28"
        >
            <motion.div style={{ y: glowY }} className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-volt/10 blur-[140px]" />
                <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-blaze/10 blur-[120px]" />
            </motion.div>
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.35]"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
                    backgroundSize: "56px 56px",
                    maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
                    WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
                }}
                aria-hidden="true"
            />

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
                    <div className="lg:col-span-6 text-center lg:text-left">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="inline-flex items-center gap-2 rounded-full border border-volt/30 bg-volt/10 px-4 py-1.5 text-xs sm:text-sm font-bold text-volt"
                            data-testid="hero-badge"
                        >
                            <Zap className="h-3.5 w-3.5" />
                            {t("hero.badge")}
                        </motion.div>

                        <h1 className="mt-6 font-display text-[clamp(2.9rem,7.5vw,6.2rem)] font-black leading-[0.95] tracking-tight text-white">
                            {[t("hero.t1"), t("hero.t2"), t("hero.t3")].map((line, i) => (
                                <span key={i} className="block overflow-hidden pb-1">
                                    <motion.span
                                        variants={lineAnim}
                                        initial="hidden"
                                        animate="show"
                                        custom={i}
                                        className={`block ${i === 1 ? "text-outline-faint" : ""} ${
                                            i === 2 ? "text-volt" : ""
                                        }`}
                                    >
                                        {line}
                                    </motion.span>
                                </span>
                            ))}
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.7, duration: 0.7 }}
                            className="mx-auto lg:mx-0 mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400"
                        >
                            {t("hero.sub")}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.85, duration: 0.7 }}
                            className="mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3"
                        >
                            <a
                                href={PHONE_TEL}
                                data-testid="hero-call-btn"
                                className="relative inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl bg-volt px-7 py-4 font-display text-base sm:text-lg font-extrabold uppercase tracking-wide text-obsidian transition-all hover:bg-volt-dark hover:-translate-y-0.5 active:scale-[0.98] shadow-[0_0_40px_rgba(212,255,0,0.3)]"
                            >
                                <span className="absolute inset-0 rounded-2xl bg-volt animate-ping-slow -z-10" />
                                <Phone className="h-5 w-5" />
                                {t("hero.call")}
                            </a>
                            <a
                                href={WA_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-wa-btn"
                                className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-7 py-4 text-base font-bold text-white backdrop-blur transition-all hover:border-whatsapp/60 hover:text-whatsapp active:scale-[0.98]"
                            >
                                <MessageCircle className="h-5 w-5" />
                                {t("hero.wa")}
                            </a>
                        </motion.div>

                        <motion.button
                            type="button"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1 }}
                            data-testid="hero-quote-link"
                            onClick={onCall}
                            className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-slate-400 transition-colors hover:text-volt"
                        >
                            {t("hero.quote")}
                            <ChevronDown className="h-4 w-4 animate-bounce" />
                        </motion.button>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.05, duration: 0.7 }}
                            className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 text-left"
                        >
                            {t("trust").map((item, i) => {
                                const Icon = TRUST_ICONS[i];
                                return (
                                    <div
                                        key={i}
                                        data-testid={`trust-card-${i}`}
                                        className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur transition-colors hover:border-volt/40"
                                    >
                                        <Icon className="h-5 w-5 text-volt" />
                                        <h3 className="mt-2 text-xs sm:text-sm font-extrabold text-white">{item.t}</h3>
                                        <p className="mt-0.5 text-[11px] leading-snug text-slate-500">{item.d}</p>
                                    </div>
                                );
                            })}
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <div className="perspective-1200" onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
                            <motion.div
                                ref={cardRef}
                                style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
                                data-testid="hero-image-card"
                                className="relative rounded-3xl border border-white/10 bg-surface p-2 shadow-[0_40px_80px_rgba(0,0,0,0.5)]"
                            >
                                <div className="relative overflow-hidden rounded-2xl">
                                    <motion.img
                                        style={{ y: imgY, scale: 1.12 }}
                                        src={IMAGES.hero}
                                        alt={t("hero.imgAlt")}
                                        className="h-[380px] sm:h-[480px] lg:h-[560px] w-full object-cover"
                                        loading="eager"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
                                    <div
                                        className="absolute inset-0 pointer-events-none"
                                        style={{
                                            background:
                                                "radial-gradient(circle at 30% 20%, rgba(212,255,0,0.12) 0%, transparent 45%)",
                                        }}
                                    />
                                    <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                                        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-obsidian/70 px-3.5 py-1.5 text-xs font-bold text-white backdrop-blur">
                                            <Zap className="h-3.5 w-3.5 text-volt" />
                                            {t("hero.serving")}
                                        </span>
                                    </div>
                                </div>

                                <div
                                    style={{ transform: "translateZ(50px)" }}
                                    className="absolute -left-3 sm:-left-6 top-8 flex items-center gap-2.5 rounded-2xl border border-volt/30 bg-obsidian/90 px-4 py-3 shadow-xl backdrop-blur animate-float"
                                >
                                    <Banknote className="h-5 w-5 text-volt" />
                                    <span className="text-xs sm:text-sm font-extrabold text-white">{t("hero.chip1")}</span>
                                </div>
                                <div
                                    style={{ transform: "translateZ(40px)" }}
                                    className="absolute -right-3 sm:-right-5 bottom-16 flex items-center gap-2.5 rounded-2xl border border-white/15 bg-obsidian/90 px-4 py-3 shadow-xl backdrop-blur animate-float"
                                >
                                    <Clock className="h-5 w-5 text-blaze" />
                                    <span className="text-xs sm:text-sm font-extrabold text-white">{t("hero.chip2")}</span>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
